// src/features/leases/leasePortfolioSort.js
//
// LSE-LANDING-PAGE-FOLLOWUP-2 (Gap A4) — Sort helpers for the Leases
// landing page. Mirrors the pattern of src/utils/sortLocations.js:
// pure functions, exported for unit testing, LEASE_SORT_OPTIONS drives
// SortDropdown so the two surfaces can never drift.
//
// Lease sort keys (smart/status-grouped is the internal default, not shown
// as a user-selectable dropdown option — mirrors sortLocations.js "smart"):
//   smart        — status group order (LEASE_STATUS_SORT_ORDER) + end-date
//                  tie-breaker (default; not user-selectable)
//   endDate      — soonest resolved end-date first
//   annualCost   — highest totalCost first (native currency, not FX-converted,
//                  so this is approximate when leases are multi-currency —
//                  acceptable for a sort UI that narrows a visible list)
//   daysRemaining — same date ordering as endDate; distinct label communicates
//                  urgency intent vs calendar intent to the user

import { resolveTimelineEndAnchor, parseDateOnly } from "./leaseTabConfig.js";
import { statusFor } from "./pages/components/LeasePortfolioFilters.jsx";

export const LEASE_SORT_KEYS = ["endDate", "annualCost", "daysRemaining"];

export const LEASE_SORT_OPTIONS = [
  { key: "endDate",       label: "End Date",       subtitle: "Soonest expiration first" },
  { key: "annualCost",    label: "Annual Cost",    subtitle: "Highest cost first" },
  { key: "daysRemaining", label: "Days Remaining", subtitle: "Most urgent first" },
];

// LSE-LANDING-STATUS-SORT-ORDER: "smart" is the internal default, mirroring
// sortLocations.js DEFAULT_SORT_KEY = "smart". Not listed in LEASE_SORT_OPTIONS
// so it does not appear as a user-selectable dropdown chip.
export const LEASE_DEFAULT_SORT_KEY = "smart";

// Status group sort order — matches the Status filter dropdown order:
// Draft → In-Use → Vacant-Held → Sublease-Out → Expired.
// Exported so column-sort handlers (and tests) can reference the same constant
// rather than duplicating the numeric assignments. Mirrors STAGE_ORDER in
// src/utils/rankSites.js. Unknown values from statusFor() fall to 99.
// LSE-LANDING-STATUS-SORT-ORDER
export const LEASE_STATUS_SORT_ORDER = {
  draft:             0,
  // LEASES-OUTLOOK-DATA-FIXES: pre-commencement sits between draft (pipeline)
  // and in_use (operational) in smart sort — signed but not yet occupying.
  pre_commencement:  1,
  in_use:            2,
  vacant_held:       3,
  expired:           5,
};

// Resolve the end-date for sorting purposes. Returns a numeric ms value
// (for comparison) or Infinity for leases with no date anchor.
function resolvedEndMs(lease) {
  const anchor = resolveTimelineEndAnchor(lease);
  if (!anchor.date) return Infinity;
  const d = parseDateOnly(anchor.date);
  return d ? d.getTime() : Infinity;
}

// Smart sort comparator: primary = status group (LEASE_STATUS_SORT_ORDER:
// draft → in_use → vacated → sublease_out → expired), secondary = ascending
// resolved end-date within each group.
// Uses statusFor() to derive the status bucket — this mirrors the same
// precedence logic used by the Status filter dropdown so "Smart" and
// the filter chips always agree on which group a lease belongs to.
// Sentinel end-dates (9999-12-31) produce a very large ms value and
// naturally sort to the bottom of their group under ascending order.
// Status column header sort should use LEASE_STATUS_SORT_ORDER when added.
export function smartSortComparator(a, b) {
  const pa = LEASE_STATUS_SORT_ORDER[statusFor(a)] ?? 99;
  const pb = LEASE_STATUS_SORT_ORDER[statusFor(b)] ?? 99;
  if (pa !== pb) return pa - pb;
  return resolvedEndMs(a) - resolvedEndMs(b);
}

export function applyLeaseSort(leases, key) {
  if (!Array.isArray(leases)) return [];
  const arr = [...leases];
  switch (key) {
    case "smart":
      return arr.sort(smartSortComparator);
    case "endDate":
    case "daysRemaining":
      return arr.sort((a, b) => resolvedEndMs(a) - resolvedEndMs(b));
    case "annualCost":
      return arr.sort((a, b) => (Number(b.totalCost) || 0) - (Number(a.totalCost) || 0));
    default: // unknown key — fall back to smart (status-group) sort
      return arr.sort(smartSortComparator);
  }
}
